import React from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';
import 'flickity/dist/flickity.css';

import gray from '../utils/gray';
import presets, { colors, baselineGrid } from '../utils/presets';
import { options, rhythm, adjustFontSizeTo } from '../utils/typography';
import { toRem, toVW, mqObj as mq } from '../utils/functions';

import Button from '../components/button';
import CheckIcon from '../assets/svg-sprite/shape-check-16px.svg';

const detailList = css`
  margin-bottom: ${rhythm(options.blockMarginBottom)};
  padding: ${toRem(baselineGrid)} calc(${toRem(baselineGrid * 3)} + ${toVW(baselineGrid)});

  &.has-title {
    padding-top: ${toRem(baselineGrid * 2)};
  }
`;

const themeMeta = css`
  ${adjustFontSizeTo('16px')};
  background-color: ${colors.ui.whisper};
  border-radius: ${presets.radius.default};
  box-shadow: 0 1px 1px 0 ${gray(84)};
`;

const detailHeader = css``;

const detailTitle = css`
  margin: 0;
  padding: ${toRem(baselineGrid * 2)} 0;
`;

const detailTable = css`
  display: block;

  [class*='cols'] & {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

const detailCell = css`
  padding: ${toRem(baselineGrid * 2)} 0;

  [class*='cols-2'] & {
    width: 50%;

    &:nth-child(2n + 1) {
      padding-right: ${toRem(baselineGrid)};
    }

    &:nth-child(2n + 2) {
      padding-left: ${toRem(baselineGrid)};
    }
  }

  [class*='split-cols'] & {
    &:nth-child(2n + 1) {
      width: 45%;
      padding-right: ${toRem(baselineGrid)};
    }

    &:nth-child(2n + 2) {
      width: 55%;
    }
  }
`;

const divider = css`
  box-shadow: inset 0 1px 0 0 ${gray(92)};
`;

const detailLine = css`
  display: flex;

  .icon {
    display: flex;
    flex: 0 0 16px;
    height: ${toRem(28)};
    margin-right: ${toRem(baselineGrid)};

    svg {
      align-self: center;
      width: ${toRem(16)};
      height: ${toRem(16)};
      fill: ${colors.accent.green};
    }
  }
`;

const DetailList = ({ title, data, cols, split, checklist }) => (
  <section
    className={cx(detailList, themeMeta, {
      checklist,
      [`cols-${cols}`]: cols,
      'split-cols': split,
      'has-title': title,
    })}
  >
    {title && (
      <header className={detailHeader}>
        <h4 className={detailTitle}>{title}</h4>
      </header>
    )}
    {data && (
      <div className={detailTable}>
        {data.map((val, idx) => (
          <div
            key={idx}
            className={cx(detailCell, () => {
              let idxAfter = 0;
              if (cols) {
                idxAfter = cols - 1;
              } else if (split) {
                idxAfter = 1;
              }
              if (!title && idx <= idxAfter) return false;
              return divider;
            })}
          >
            {checklist ? (
              <div className={detailLine}>
                <div className={'icon'}>
                  <svg viewBox={CheckIcon.viewBox}>
                    <use xlinkHref={`#${CheckIcon.id}`} />
                  </svg>
                </div>
                <div>{val}</div>
              </div>
            ) : (
              val
            )}
          </div>
        ))}
      </div>
    )}
  </section>
);

DetailList.propTypes = {
  title: PropTypes.string,
  data: PropTypes.array.isRequired,
  cols: PropTypes.number,
  split: PropTypes.bool,
  checklist: PropTypes.bool,
};

const ThemeDetails = props => {
  const { name, price, purchaseURL, created, updated, version, details } = props;

  const purchaseBox = css`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: ${rhythm(options.blockMarginBottom * 1.5)};
  `;

  const purchaseBoxTitle = css`
    margin: 0;
    padding: ${toRem(baselineGrid)};
    padding-left: 0;
    font-size: ${toRem(24)};
  `;

  const purchaseBoxButton = css`
    margin: 0;
    padding: ${toRem(baselineGrid * 2)} ${toRem(baselineGrid * 4)};
    font-size: ${toRem(18)};
  `;

  const buttonMobile = css`
    display: block;

    ${mq.desk} {
      display: none;
    }
  `;

  const buttonDesktop = css`
    display: none;

    ${mq.desk} {
      display: block;
    }
  `;

  return (
    <div>
      <div className={purchaseBox}>
        <h4 className={purchaseBoxTitle}>{name}</h4>
        <Button
          to={purchaseURL}
          className={cx(purchaseBoxButton, buttonMobile)}
          shadow
          gradient={{
            rotate: -16,
            lighten: 0.08,
          }}
        >{`Buy $${price}`}</Button>
        <Button
          to={purchaseURL}
          className={cx(purchaseBoxButton, buttonDesktop)}
          shadow
          gradient={{
            rotate: -16,
            lighten: 0.08,
          }}
        >{`Buy Theme $${price}`}</Button>
      </div>
      <DetailList
        data={['Created on', created, 'Last updated', updated, 'Current version', version]}
        split
      />
      <DetailList title={'What you get:'} data={details} checklist />
    </div>
  );
};

ThemeDetails.propTypes = {
  name: PropTypes.string,
  price: PropTypes.string,
  demos: PropTypes.array,
  purchaseURL: PropTypes.string,
  created: PropTypes.string,
  updated: PropTypes.string,
  version: PropTypes.string,
  details: PropTypes.array,
  platform: PropTypes.string,
  tags: PropTypes.array,
};

export default ThemeDetails;

export const themeDetailsFragment = graphql`
  fragment themeDetails_details on MarkdownRemark {
    frontmatter {
      name
      created(formatString: "MMMM DD, YYYY")
      updated(formatString: "MMMM DD, YYYY")
      version
      details
      demos
      platform
      price
      purchaseURL
      tags
    }
  }
`;



// WEBPACK FOOTER //
// ./src/components/theme-details.js